import React, { useCallback, memo, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Outlet } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown, FaPlay } from 'react-icons/fa';
import '../../../styles/SearchOrders/customerOrders.scss';
import { encodeData } from '../../../helpers/encoderDecoder';

// Constants
const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// Sort directions
const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
  NONE: 'none',
};

// Animation variants for the button
const buttonVariants = {
  initial: { scale: 1 },
  hover: { scale: 1.03 },
  tap: { scale: 0.97 },
};

/**
 * Table row component for displaying order data
 */
const TableRow = memo(({ data, email, token }) => {
  const { orderID, externalOrderId, deliveryDate, orderTotal } = data;
  const formattedDate = deliveryDate.split(' ')[0] || deliveryDate;
  const formattedTotal = CURRENCY_FORMATTER.format(orderTotal);

  const handleLinkClick = useCallback(
    async event => {
      event.preventDefault();

      try {
        // Process all encodings concurrently for better performance
        const [encodedInternalState, encodedExternalState, encodedEmailState, encodedTokenState] =
          await Promise.all([
            encodeData(String(orderID)),
            encodeData(externalOrderId),
            encodeData(email),
            encodeData(token),
          ]);

        // Construct the query parameters
        const params = new URLSearchParams({
          intState: encodedInternalState,
          extState: encodedExternalState,
          emaState: encodedEmailState,
          tkmState: encodedTokenState,
        }).toString();

        // Open the new window with the path and query parameters
        const path = `/pages/share-your-wedding-flair-entry?${params}`;
        window.open(path, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Error processing order link:', error);
        // Consider adding a toast notification here for user feedback
        alert('Unable to open the story page. Please try again later.');
      }
    },
    [orderID, externalOrderId, email, token]
  );

  return (
    <tr>
      <td className="orderTD" headers="column-orderid" data-label="Order ID:">
        {externalOrderId}
      </td>
      <td className="dateTD" headers="column-date" data-label="Delivery Date:">
        {formattedDate}
      </td>
      <td className="totalOrderTD" headers="column-total" data-label="Order Total:">
        {formattedTotal}
      </td>
      <td style={{ whiteSpace: 'nowrap' }} headers="column-actions" data-label="">
        <button
          className="we-startstory ff-button--primary"
          onClick={handleLinkClick}
          aria-label={`Start story for order ${externalOrderId}`}
          title={`Create story for order placed on ${formattedDate}`}
          variants={buttonVariants}
          initial="initial"
          whileHover="hover"
          whileTap="tap">
          <FaPlay size={14} className="icon-white size-small" />
          <span>START STORY</span>
        </button>
      </td>
    </tr>
  );
});

TableRow.displayName = 'TableRow';

TableRow.propTypes = {
  data: PropTypes.shape({
    orderID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    externalOrderId: PropTypes.string.isRequired,
    deliveryDate: PropTypes.string.isRequired,
    orderTotal: PropTypes.number.isRequired,
  }).isRequired,
  email: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
};

/**
 * Table header component for the orders table
 */
const TableHeader = memo(({ sortConfig, onSort }) => {
  const getSortIcon = columnName => {
    if (sortConfig.key !== columnName) {
      return <FaSort size={14} className="size-small icon-color-ff-button-primary" />;
    }
    return sortConfig.direction === SORT_DIRECTIONS.ASC ? (
      <FaSortUp size={14} className="size-small icon-color-ff-button-primary" />
    ) : (
      <FaSortDown size={14} className="size-small icon-color-ff-button-primary" />
    );
  };

  const getAriaLabel = columnName => {
    const columnLabels = {
      externalOrderId: 'Order ID',
      deliveryDate: 'Delivery Date',
      orderTotal: 'Order Total',
    };

    const columnLabel = columnLabels[columnName] || columnName;

    if (sortConfig.key !== columnName) {
      return `Sort by ${columnLabel}`;
    }

    return sortConfig.direction === SORT_DIRECTIONS.ASC
      ? `${columnLabel} sorted ascending, select to sort descending`
      : `${columnLabel} sorted descending, select to clear sort`;
  };

  const handleSortClick = columnName => {
    onSort(columnName);
  };

  return (
    <thead>
      <tr className="headerRow">
        <th
          scope="col"
          id="column-orderid"
          className={`sortable-header ${sortConfig.key === 'externalOrderId' ? 'active-sort' : ''}`}
          onClick={() => handleSortClick('externalOrderId')}
          aria-sort={sortConfig.key === 'externalOrderId' ? sortConfig.direction : 'none'}
          aria-label={getAriaLabel('externalOrderId')}
          title={getAriaLabel('externalOrderId')}>
          <span className="header-text">Order ID</span>
          <span className="sort-icon">{getSortIcon('externalOrderId')}</span>
        </th>
        <th
          scope="col"
          id="column-date"
          className={`sortable-header ${sortConfig.key === 'deliveryDate' ? 'active-sort' : ''}`}
          onClick={() => handleSortClick('deliveryDate')}
          aria-sort={sortConfig.key === 'deliveryDate' ? sortConfig.direction : 'none'}
          aria-label={getAriaLabel('deliveryDate')}
          title={getAriaLabel('deliveryDate')}>
          <span className="header-text">Delivery Date</span>
          <span className="sort-icon">{getSortIcon('deliveryDate')}</span>
        </th>
        <th
          scope="col"
          id="column-total"
          className={`sortable-header ${sortConfig.key === 'orderTotal' ? 'active-sort' : ''}`}
          onClick={() => handleSortClick('orderTotal')}
          aria-sort={sortConfig.key === 'orderTotal' ? sortConfig.direction : 'none'}
          aria-label={getAriaLabel('orderTotal')}
          title={getAriaLabel('orderTotal')}>
          <span className="header-text">Order Total</span>
          <span className="sort-icon">{getSortIcon('orderTotal')}</span>
        </th>
        <th scope="col" id="column-actions" className="actionCol">
          <span className="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
  );
});

TableHeader.displayName = 'TableHeader';

TableHeader.propTypes = {
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
  onSort: PropTypes.func.isRequired,
};

/**
 * Empty state header component
 */
const EmptyHeader = memo(() => (
  <thead>
    <tr className="emptyHeaderRow">
      <th scope="col" colSpan="4" id="empty-message">
        No Orders Found
      </th>
    </tr>
  </thead>
));

EmptyHeader.displayName = 'EmptyHeader';

/**
 * Main CustomerOrders component that displays a table of orders
 */
export const CustomerOrders = ({ data = [], token, email }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'externalOrderId',
    direction: SORT_DIRECTIONS.ASC,
  });

  const handleSort = useCallback(columnName => {
    setSortConfig(prevSortConfig => {
      // If clicking the same column, toggle direction
      if (prevSortConfig.key === columnName) {
        // Cycle through: ASC -> DESC -> NONE (reset to default sort) -> ASC
        if (prevSortConfig.direction === SORT_DIRECTIONS.ASC) {
          return { key: columnName, direction: SORT_DIRECTIONS.DESC };
        } else if (prevSortConfig.direction === SORT_DIRECTIONS.DESC) {
          return { key: 'externalOrderId', direction: SORT_DIRECTIONS.ASC };
        }
      }
      // New column, start with ASC
      return { key: columnName, direction: SORT_DIRECTIONS.ASC };
    });
  }, []);

  // Sort the data
  const sortedData = useMemo(() => {
    if (!data.length) return [];

    const sortableData = [...data];
    const { key, direction } = sortConfig;

    if (direction === SORT_DIRECTIONS.NONE) {
      return sortableData;
    }

    return sortableData.sort((a, b) => {
      // Handle different data types
      if (key === 'deliveryDate') {
        // Date comparison
        const dateA = new Date(a[key]);
        const dateB = new Date(b[key]);

        return direction === SORT_DIRECTIONS.ASC ? dateA - dateB : dateB - dateA;
      } else if (key === 'orderTotal') {
        // Numeric comparison
        return direction === SORT_DIRECTIONS.ASC ? a[key] - b[key] : b[key] - a[key];
      } else {
        // String comparison (case insensitive)
        const valueA = String(a[key]).toLowerCase();
        const valueB = String(b[key]).toLowerCase();

        if (valueA < valueB) {
          return direction === SORT_DIRECTIONS.ASC ? -1 : 1;
        }
        if (valueA > valueB) {
          return direction === SORT_DIRECTIONS.ASC ? 1 : -1;
        }
        return 0;
      }
    });
  }, [data, sortConfig]);

  const hasOrders = sortedData.length > 0;

  return (
    <div className="ordersCustomer" role="region" aria-label="Your Order History">
      <div className="table-border-customer">
        <Table
          responsive
          hover
          className="expandedTable we-table table-cell"
          aria-labelledby="orders-table-caption">
          <caption id="orders-table-caption" className="sr-only">
            {hasOrders
              ? `Your orders - ${sortedData.length} order${
                  sortedData.length === 1 ? '' : 's'
                } found`
              : 'No orders found for your account'}
          </caption>

          {hasOrders ? (
            <TableHeader sortConfig={sortConfig} onSort={handleSort} />
          ) : (
            <EmptyHeader />
          )}

          <tbody>
            {hasOrders &&
              sortedData.map((orderData, index) => (
                <TableRow
                  key={`order-${orderData.orderID || index}`}
                  data={orderData}
                  email={email}
                  token={token}
                />
              ))}
            {hasOrders === false && (
              <tr>
                <td colSpan="4" className="no-orders-message">
                  No orders found for this email address. If you recently placed an order, it may
                  take some time to appear.
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        <Outlet />
      </div>
    </div>
  );
};

CustomerOrders.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      orderID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      externalOrderId: PropTypes.string.isRequired,
      deliveryDate: PropTypes.string.isRequired,
      orderTotal: PropTypes.number.isRequired,
    })
  ).isRequired,
  token: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

export default CustomerOrders;
